import { Clock, Shield, Sparkles, Users, CheckCircle2, Zap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";

const translations = {
  de: {
    badge: "Unsere Vorteile",
    title: "Was bietet das EAP® Abutment?",
    subtitle: "Beste Zeiteffizienz und Rot-Weiß-Ästhetik",
    quote: "\u201EW\u00E4hrend viele Abutment-Systeme einzelne evidenzbasierte Vorteile bieten, integriert EAP\u00AE systematisch die entscheidenden biologischen Erfolgsfaktoren \u2013 Biokompatibilit\u00E4t, kontrollierte Klebefuge und One-Abutment-One-Time \u2013 in einem einzigen Design.\u201C",
    features: [
      {
        title: "Bester Workflow",
        description: "Egal ob analog oder digital. Enorme Zeitersparnis bei gleichzeitig höchster Rot-Weiß-Ästhetik.",
      },
      {
        title: "Patientenindividuelles Emergenzprofil",
        description: "Einfach und unkompliziert wie bei Standard-Abutments. Patientenspezifische Emergenzprofile für natürlich aussehende Implantatversorgungen.",
      },
      {
        title: "Nachträglich veränderbar",
        description: "Bei Bedarf nachträglich veränderbar in Minuten, ohne die Krone neu machen zu müssen.",
      },
      {
        title: "Materialunabhängig",
        description: "Sie verwenden Ihr gewohntes Kronenmaterial. Maßgeschneiderte Abutments für optimale Funktionalität und Ästhetik.",
      },
      {
        title: "Weltweit patentiert",
        description: "Unser einzigartiges EAP® Abutment und der Produktionsprozess sind international geschützt.",
      },
      {
        title: "Biologische Verträglichkeit",
        description: "Durch modernste Technologien und elektronmikroskopische Analysen für biologische Verträglichkeit auf höchstem Niveau.",
      },
    ],
  },
  en: {
    badge: "Our Advantages",
    title: "What does the EAP® Abutment offer?",
    subtitle: "Best time efficiency and red-white aesthetics",
    quote: `\u201CWhile many abutment systems offer individual evidence-based advantages, EAP\u00AE systematically integrates the decisive biological success factors \u2013 biocompatibility, controlled adhesive joint, and One-Abutment-One-Time \u2013 into a single design.\u201D`,
    features: [
      {
        title: "Best Workflow",
        description: "Whether analog or digital. Enormous time savings with the highest red-white aesthetics.",
      },
      {
        title: "Patient-specific Emergence Profile",
        description: "Simple and uncomplicated like standard abutments. Patient-specific emergence profiles for natural-looking implant restorations.",
      },
      {
        title: "Modifiable Later",
        description: "Can be modified later in minutes if needed, without having to remake the crown.",
      },
      {
        title: "Material Independent",
        description: "Use your familiar crown material. Custom abutments for optimal functionality and aesthetics.",
      },
      {
        title: "Worldwide Patented",
        description: "Our unique EAP® Abutment and production process are internationally protected.",
      },
      {
        title: "Biological Compatibility",
        description: "Through state-of-the-art technologies and electron microscopic analyses for biological compatibility at the highest level.",
      },
    ],
  },
  fr: {
    badge: "Nos Avantages",
    title: "Que propose l'Abutment EAP®?",
    subtitle: "Meilleure efficacité temporelle et esthétique rouge-blanc",
    quote: `\u00AB Alors que de nombreux syst\u00E8mes de piliers offrent des avantages individuels fond\u00E9s sur des preuves, EAP\u00AE int\u00E8gre syst\u00E9matiquement les facteurs biologiques d\u00E9cisifs de succ\u00E8s \u2013 biocompatibilit\u00E9, joint adh\u00E9sif contr\u00F4l\u00E9 et One-Abutment-One-Time \u2013 dans un seul design. \u00BB`,
    features: [
      {
        title: "Meilleur Workflow",
        description: "Que ce soit analogique ou numérique. Énorme gain de temps avec la plus haute esthétique rouge-blanc.",
      },
      {
        title: "Profil d'Émergence Individuel",
        description: "Simple et sans complications comme les piliers standard. Profils d'émergence spécifiques au patient pour des restaurations implantaires d'apparence naturelle.",
      },
      {
        title: "Modifiable Ultérieurement",
        description: "Peut être modifié ultérieurement en quelques minutes si nécessaire, sans refaire la couronne.",
      },
      {
        title: "Indépendant du Matériau",
        description: "Utilisez votre matériau de couronne habituel. Piliers sur mesure pour une fonctionnalité et une esthétique optimales.",
      },
      {
        title: "Breveté Mondialement",
        description: "Notre pilier EAP® unique et le processus de production sont protégés internationalement.",
      },
      {
        title: "Compatibilité Biologique",
        description: "Grâce aux technologies de pointe et aux analyses microscopiques électroniques pour une compatibilité biologique au plus haut niveau.",
      },
    ],
  },
  nl: {
    badge: "Onze Voordelen",
    title: "Wat biedt het EAP® Abutment?",
    subtitle: "Beste tijdsefficiëntie en rood-witte esthetiek",
    quote: `\u201CTerwijl veel abutmentsystemen individuele evidence-based voordelen bieden, integreert EAP\u00AE systematisch de beslissende biologische succesfactoren \u2013 biocompatibiliteit, gecontroleerde hechtvoeg en One-Abutment-One-Time \u2013 in \u00E9\u00E9n enkel design.\u201D`,
    features: [
      {
        title: "Beste Workflow",
        description: "Of het nu analoog of digitaal is. Enorme tijdsbesparing met de hoogste rood-witte esthetiek.",
      },
      {
        title: "Patiëntspecifiek Emergentieprofiel",
        description: "Eenvoudig en ongecompliceerd zoals standaard abutments. Patiëntspecifieke emergentieprofielen voor natuurlijk ogende implantaatrestauraties.",
      },
      {
        title: "Later Aanpasbaar",
        description: "Kan indien nodig later in minuten worden aangepast, zonder de kroon opnieuw te hoeven maken.",
      },
      {
        title: "Materiaal Onafhankelijk",
        description: "Gebruik uw vertrouwde kroonmateriaal. Aangepaste abutments voor optimale functionaliteit en esthetiek.",
      },
      {
        title: "Wereldwijd Gepatenteerd",
        description: "Ons unieke EAP® Abutment en productieproces zijn internationaal beschermd.",
      },
      {
        title: "Biologische Compatibiliteit",
        description: "Door middel van state-of-the-art technologieën en elektronenmicroscopische analyses voor biologische compatibiliteit op het hoogste niveau.",
      },
    ],
  },
  it: {
    badge: "I Nostri Vantaggi",
    title: "Cosa offre l'Abutment EAP®?",
    subtitle: "Migliore efficienza temporale ed estetica rosso-bianco",
    quote: `\u201CMentre molti sistemi di abutment offrono singoli vantaggi basati sull\u2019evidenza, EAP\u00AE integra sistematicamente i fattori biologici decisivi per il successo \u2013 biocompatibilit\u00E0, giunto adesivo controllato e One-Abutment-One-Time \u2013 in un unico design.\u201D`,
    features: [
      {
        title: "Miglior Workflow",
        description: "Sia analogico che digitale. Enorme risparmio di tempo con la massima estetica rosso-bianco.",
      },
      {
        title: "Profilo di Emergenza Individuale",
        description: "Semplice e senza complicazioni come gli abutment standard. Profili di emergenza specifici per il paziente per restauri implantari dall'aspetto naturale.",
      },
      {
        title: "Modificabile Successivamente",
        description: "Può essere modificato successivamente in pochi minuti se necessario, senza dover rifare la corona.",
      },
      {
        title: "Indipendente dal Materiale",
        description: "Utilizzate il vostro materiale per corone abituale. Abutment personalizzati per funzionalità ed estetica ottimali.",
      },
      {
        title: "Brevettato Mondialmente",
        description: "Il nostro unico abutment EAP® e il processo di produzione sono protetti internazionalmente.",
      },
      {
        title: "Compatibilità Biologica",
        description: "Attraverso tecnologie all'avanguardia e analisi microscopiche elettroniche per la compatibilità biologica al massimo livello.",
      },
    ],
  },
  es: {
    badge: "Nuestras Ventajas",
    title: "¿Qué ofrece el Abutment EAP®?",
    subtitle: "Mejor eficiencia temporal y estética rojo-blanco",
    quote: `\u201CMientras muchos sistemas de pilares ofrecen ventajas individuales basadas en evidencia, EAP\u00AE integra sistem\u00E1ticamente los factores biol\u00F3gicos decisivos para el \u00E9xito \u2013 biocompatibilidad, junta adhesiva controlada y One-Abutment-One-Time \u2013 en un solo dise\u00F1o.\u201D`,
    features: [
      {
        title: "Mejor Flujo de Trabajo",
        description: "Ya sea analógico o digital. Enorme ahorro de tiempo con la más alta estética rojo-blanco.",
      },
      {
        title: "Perfil de Emergencia Individual",
        description: "Simple y sin complicaciones como los pilares estándar. Perfiles de emergencia específicos del paciente para restauraciones de implantes de aspecto natural.",
      },
      {
        title: "Modificable Posteriormente",
        description: "Puede modificarse posteriormente en minutos si es necesario, sin tener que rehacer la corona.",
      },
      {
        title: "Independiente del Material",
        description: "Use su material de corona habitual. Pilares personalizados para funcionalidad y estética óptimas.",
      },
      {
        title: "Patentado Mundialmente",
        description: "Nuestro único pilar EAP® y el proceso de producción están protegidos internacionalmente.",
      },
      {
        title: "Compatibilidad Biológica",
        description: "A través de tecnologías de vanguardia y análisis microscópicos electrónicos para compatibilidad biológica al más alto nivel.",
      },
    ],
  },
};

const featureIcons = [Clock, Sparkles, Users, CheckCircle2, Shield, Zap];

export const Features = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <section id="vorteile" className="py-12 sm:py-16 lg:py-20 bg-secondary/30">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12 lg:mb-16 animate-fade-up">
          <span className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            {t.badge}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4">
            {t.title}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto px-2">
            {t.subtitle}
          </p>
        </div>

        <div className="mb-10 sm:mb-12 lg:mb-16 max-w-4xl mx-auto">
          <blockquote className="relative border-l-4 border-primary pl-6 sm:pl-8 py-4 sm:py-6 bg-primary/5 rounded-r-xl">
            <p className="text-base sm:text-lg lg:text-xl text-foreground font-medium italic leading-relaxed">
              {t.quote}
            </p>
          </blockquote>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {t.features.map((feature, index) => {
            const Icon = featureIcons[index];
            return (
              <Card
                key={index}
                className="p-5 sm:p-6 lg:p-8 hover:shadow-glow transition-all duration-300 hover:-translate-y-2 bg-card border-border animate-fade-in group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 sm:mb-5 lg:mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 sm:mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
